import { Check, Copy, ExternalLink, Link2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

/**
 * One join link, with the two things an admin does with it: copy it into an
 * email, or open it to check the room works.
 *
 * The copy state lives here rather than in the parent so the host and guest
 * rows cannot both flash "Copied" at once, which is exactly the moment someone
 * pastes the wrong one to a candidate.
 */
export function JoinLinkRow({
  label,
  description,
  url,
  openLabel,
}: {
  label: string;
  description: string;
  url: string;
  openLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resetTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    },
    [],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setError(null);
      setCopied(true);
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy automatically. Select the link and copy it manually.');
    }
  };

  return (
    <div className="border-border/60 rounded-xl border bg-white/[0.03] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase">
          <Link2 className="size-3" aria-hidden />
          {label}
        </span>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={copy}
            aria-label={`Copy ${label.toLowerCase()} join link`}
          >
            {copied ? <Check className="size-4 text-[var(--success)]" /> : <Copy className="size-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>

          <Button variant="ghost" size="sm" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer" aria-label={openLabel}>
              <ExternalLink className="size-4" />
              Open
            </a>
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground mt-1 text-xs text-pretty">{description}</p>
      <p className="mt-1.5 font-mono text-xs break-all select-all">{url}</p>

      {error && (
        <p
          role="alert"
          className="mt-2 text-xs text-[color-mix(in_oklab,var(--destructive)_80%,white)] text-pretty"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export const HOST_LINK_DESCRIPTION =
  'Opening this joins the real interview as the interviewer, with host controls.';

export const GUEST_LINK_DESCRIPTION = 'Opening this joins the real interview as the candidate.';
